<template>
  <main-layout
    controlsBg="light"
    :hasControlsBar="false"
    :breadcrumb="getBreadcrumb">
    <div class="container">
      <grid-row column-align="center">
        <grid-col col-size="4">
          <h1 class="header margin-top-xl">
            <chevron
              @click="$router.push({name: 'UserList'})"/>
            {{ isEdition() ? $t('user.editFormTitle') : $t('user.addFormTitle') }}
          </h1>
          <alert
            v-if="alertVisible"
            :message="alertMessage"
            :type="alertType">
          </alert>
        </grid-col>
      </grid-row>
      <form
        v-on:submit.prevent
        v-on:keypress.enter.prevent="onSave"
        v-on:keydown.space.prevent="'false'">
        <grid-row column-align="center">
          <grid-col col-size="4">
            <div class="variable-input-field variable-input-field-name">
              <label-input
                :lowercase="true"
                :disabled="isEdition()"
                ref="nameInput"
                form-size="m"
                :label="$t('user.nameLabel')"
                v-model="name"
                :type="'text'"
                :error="nameErrorLenght || nameErrorUnique"
                :errorMessage="displayNameErrorMessage">
              </label-input>
            </div>
          </grid-col>
        </grid-row>
        <grid-row column-align="center" col-spaced>
          <grid-col col-size="2">
            <div class="variable-input-field variable-input-field-name">
              <label-input
                ref="passwordInput"
                form-size="m"
                :label="$t('user.password')"
                v-on:click="onClickPassword"
                v-model="password"
                :type="'password'"
                :error="passwordError"
                :errorMessage="$t('user.passwordFormatMessage')"
                autocomplete="new-password">
              </label-input>
            </div>
          </grid-col>
          <grid-col col-size="2">
            <div class="variable-input-field variable-input-field-name">
              <label-input
                ref="passwordRepeatInput"
                form-size="m"
                :label="$t('user.passwordRepeat')"
                v-on:click="onClickPassword"
                v-model="passwordRepeat"
                :type="'password'"
                :error="passwordRepeatError"
                :errorMessage="$t('user.passwordShouldBeEqual')"
                autocomplete="new-password">
              </label-input>
            </div>
          </grid-col>
        </grid-row>
        <grid-row column-align="center">
          <grid-col col-size="4">
            <div class="variable-input-field variable-input-field-name">
              <label-input
                :lowercase="true"
                ref="emailInput"
                form-size="m"
                :label="$t('signin.email')"
                v-model="email"
                :type="'text'"
                :error="emailError"
                :errorMessage="$t('commons.mandatoryFieldMessage')">
              </label-input>
            </div>
          </grid-col>
        </grid-row>
        <grid-row column-align="center">
          <grid-col col-size="4">
            <div class="variable-input-field variable-input-field-name">
              <label-input
                :lowercase="true"
                ref="prescriptorInput"
                form-size="m"
                :label="$t('health.userProfile.prescriptor')"
                v-model="prescriptorCode"
                :type="'text'"
                :error="prescriptorError"
                :errorMessage="$t('health.userProfile.prescriptorDoesNotMatchUser')">
              </label-input>
            </div>
          </grid-col>
        </grid-row>
        <grid-row column-align="center" col-spaced>
          <grid-col col-size="4">
            <div class="">
              <h3 class="margin-top-m">{{ $t('health.updateLicense.subscriptions') }}:
                {{ getSubscriptionsLabeled() }}</h3>
            </div>
          </grid-col>
        </grid-row>

        <grid-row column-align="center" col-spaced v-if="getSubscriptionSelectOptions.length > 0">
          <grid-col col-size="2">
            <div class="">
              <h3 class="margin-top-m">{{ $t('health.updateLicense.addSubscription') }}: </h3>
            </div>
          </grid-col>
          <grid-col col-size="1">
            <v-select class="v-select--light"
                      :onChange="changeSubscription"
                      :searchable="false"
                      :options="getSubscriptionSelectOptions"></v-select>
          </grid-col>
          <grid-col col-size="1">
            <v-select class="v-select--light"
                      :onChange="changeSubscriptionDuration"
                      :searchable="false"
                      :options="getDurationOptions"></v-select>
          </grid-col>
        </grid-row>

        <grid-row column-align="center" col-spaced v-if="isEdition()">
          <grid-col col-size="4">
            <div class="">
              <h3 class="margin-top-m">{{ $t('health.userProfile.address') }}:
                {{ getAddress() }}</h3>
            </div>
          </grid-col>
        </grid-row>

        <grid-row column-align="center" class="margin-top-xl">
          <grid-col col-size="1">
            <div class="variable-input-field submit-buttons align-right margin-bottom-xxl">
              <checkbox
                id="admin-enabled"
                :label="$t('user.admin')"
                v-on:change="onAdminChnage"
                :checked="admin">
              </checkbox>
            </div>
          </grid-col>
          <grid-col col-size="1">
            <div class="variable-input-field submit-buttons align-right margin-bottom-xxl">
              <checkbox
                id="xugarkid-enabled"
                :label="$t('user.xugarkid')"
                v-on:change="onXugarkidChange"
                :checked="xugarkid">
              </checkbox>
            </div>
          </grid-col>
          <grid-col col-size="1">
            <div class="variable-input-field submit-buttons align-right margin-bottom-xxl">
              <checkbox
                id="nutri-enabled"
                :label="$t('user.nutri')"
                v-on:change="onNutriChange"
                :checked="nutri">
              </checkbox>
            </div>
          </grid-col>
          <grid-col col-size="1">
            <div class="variable-input-field submit-buttons align-right margin-bottom-xxl">
              <checkbox
                id="prescriptor-enabled"
                :label="$t('user.prescriptor')"
                v-on:change="onPrescriptorChange"
                :checked="prescriptor">
              </checkbox>
            </div>
          </grid-col>
        </grid-row>
        <grid-row column-align="center">
          <grid-col col-size="4">
            <div class="variable-input-field submit-buttons align-right">
              <btn-pill
                :btnText="$t('commons.cancel')"
                v-on:click="onCancel"
                btn-size="m"
                btn-type="secondary">
              </btn-pill>
              <btn-pill
                :btnText="$t('commons.save')"
                v-on:click="onSave"
                btn-size="m">
              </btn-pill>
            </div>
          </grid-col>
        </grid-row>
      </form>
    </div>
  </main-layout>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import MainLayout from '@/layouts/MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import LabelInput from '@/components/shared/forms/LabelInput';
import LabelSelect from '@/components/shared/forms/LabelSelect';
import Radio from '@/components/shared/forms/Radio';
import ToggleSwitch from '@/components/shared/forms/ToggleSwitch';
import BtnPill from '@/components/shared/buttons/BtnPill';
import Checkbox from '@/components/shared/forms/Checkbox';
import Alert from '@/components/shared/alert/Alert';
import Chevron from '@/components/shared/Chevron';
import VueDatepickerLocal from 'vue-datepicker-local';
import licenseApi from 'BRAND/api/updateLicense';
import addressApi from 'BRAND/api/address';
import vSelect from 'vue-select';

export default {
  components: {
    MainLayout,
    GridRow,
    GridCol,
    LabelInput,
    LabelSelect,
    Radio,
    ToggleSwitch,
    BtnPill,
    Checkbox,
    Alert,
    Chevron,
    VueDatepickerLocal,
    vSelect
  },
  props: {
    dateValue: {
      type: Date,
      default: () => new Date()
    }
  },
  data () {
    return {
      user: {},
      name: '',
      password: '',
      passwordRepeat: '',
      email: '',
      prescriptorCode: '',
      admin: false,
      xugarkid: false,
      nutri: false,
      prescriptor: false,
      subscriptions: [],
      selectedSubscription: null,
      selectedSubscriptionDuration: null,
      address: null,
      /* Form field errors */
      nameErrorLenght: false,
      nameErrorUnique: false,
      passwordError: false,
      passwordRepeatError: false,
      emailError: false,
      prescriptorError: false,
      /* UI controls */
      alertVisible: false,
      alertMessage: '',
      alertType: '' // error, warning, success
    };
  },
  computed: {
    getBreadcrumb () {
      return [
        {
          name: this.$t('menu.settings'),
          link: '/settings'
        },
        {
          name: this.$t('user.title'),
          link: '/user/list'
        },
        {
          name: this.isEdition() ? this.$t('user.editFormTitle') : this.$t('user.addFormTitle'),
          link: '/user',
          disabled: true
        }
      ];
    },
    displayNameErrorMessage () {
      let errorMessage = this.$t('commons.mandatoryFieldMessage');
      if (this.nameErrorUnique) {
        errorMessage = this.$t('user.userAlreadyExists');
      }
      return errorMessage;
    },
    getDurationOptions () {
      return [
        {
          value: 'daily',
          label: this.$t('health.subscription.daily')
        },
        {
          value: 'three_day',
          label: this.$t('health.subscription.threeDays')
        },
        {
          value: 'weekly',
          label: this.$t('health.subscription.weekly')
        },
        {
          value: 'monthly',
          label: this.$t('health.subscription.monthly')
        },
        {
          value: 'two_month',
          label: this.$t('health.subscription.twoMonths')
        },
        {
          value: 'three_month',
          label: this.$t('health.subscription.threeMonths')
        },
        {
          value: 'six_month',
          label: this.$t('health.subscription.sixMonths')
        },
        {
          value: 'yearly',
          label: this.$t('health.subscription.yearly')
        },
        {
          value: 'lifetime',
          label: this.$t('health.subscription.lifetime')
        }];
    },
    getSubscriptionSelectOptions () {
      return [
        {
          value: 'viber',
          label: 'Viber'
        },
        {
          value: 'viberPlus',
          label: 'Viber Plus+'
        }
      ].filter(value => !this.subscriptions.includes(value.label));
    }
  },
  created () {
    this.user = this.$route.params;
    if (this.isEdition()) {
      this.name = this.user.name;
      this.admin = this.user.admin;
      this.xugarkid = this.user.xugarkid;
      this.prescriptor = this.user.prescriptor;
      this.nutri = this.user.nutri;
      this.email = this.user.email;
      this.prescriptorCode = this.user.prescriptorCode;

      this.password = PASSWORD_DEFAULT_VALUE;
      this.passwordRepeat = PASSWORD_DEFAULT_VALUE;
      licenseApi.getSubscriptionsOfUser(this, this.user.id).then((response) => {
        this.subscriptions = response;
      });
      addressApi.getUserAddress(this, this.user.id).then((response) => {
        this.address = response;
      });
    }
  },
  mounted () {
    this.$refs.nameInput.focus();
  },
  methods: {
    ...mapActions([
      'createUser',
      'updateUser'
    ]),
    ...mapGetters([
      'getLanguageCode'
    ]),
    isEdition () {
      return this.user.id !== undefined;
    },
    onSave () {
      clearErrors(this);
      if (isValidForm(this)) {
        this.user.name = this.name.toLowerCase();
        this.user.admin = this.admin;
        this.user.xugarkid = this.xugarkid;
        this.user.nutri = this.nutri;
        this.user.prescriptor = this.prescriptor;
        this.user.password = this.password !== PASSWORD_DEFAULT_VALUE ? this.password : undefined;
        this.user.email = this.email;
        this.user.prescriptorCode = this.prescriptorCode;
        this.user.subscription = this.selectedSubscription;
        this.user.subscriptionDuration = this.selectedSubscriptionDuration;
        if (this.isEdition()) {
          updateUser(this, this.user);
        } else {
          createUser(this, this.user);
        }
      }
    },
    onCancel () {
      this.$router.push({name: 'UserList'});
    },
    onAdminChnage (value) {
      this.admin = value;
    },
    onXugarkidChange (value) {
      this.xugarkid = value;
    },
    onNutriChange (value) {
      this.nutri = value;
    },
    onPrescriptorChange (value) {
      this.prescriptor = value;
    },
    onClickPassword () {
      if (this.password === PASSWORD_DEFAULT_VALUE) {
        this.password = '';
        this.passwordRepeat = '';
      }
    },
    capitalize (word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    },
    removeLastCharacter (str) {
      return str.substring(0, str.length - 1);
    },
    changeSubscription (value) {
      this.selectedSubscription = value.value;
    },
    changeSubscriptionDuration (value) {
      this.selectedSubscriptionDuration = value.value;
    },
    getSubscriptionsLabeled () {
      return [
        {
          value: 'viber',
          label: 'Viber'
        },
        {
          value: 'viberPlus',
          label: 'Viber Plus+'
        }
      ].filter(value => this.subscriptions.includes(value.value)).map(value => value.label).join(', ');
    },
    getAddress () {
      if (this.address !== null && this.address !== '') {
        return this.address.street + ', ' + this.address.city + ', ' + this.address.postal_code + ', ' + this.address.province + ', ' + this.address.country;
      }
      return this.$t('health.userProfile.noAddress');
    }
  }
};

const PASSWORD_DEFAULT_VALUE = 'passw0rd';

const clearErrors = (context) => {
  context.nameErrorLenght = false;
  context.nameErrorUnique = false;
  context.emailError = false;
  context.prescriptorError = false;
  context.passwordError = false;
  context.passwordRepeatError = false;
};

const isValidForm = (context) => {
  let valid = true;
  if (!(context.name && context.name.length > 0)) {
    valid = false;
    context.nameErrorLenght = true;
  }

  if (!(context.password && isValidPassword(context))) {
    valid = false;
    context.passwordError = true;
  }
  if (!(context.password === context.passwordRepeat)) {
    valid = false;
    context.passwordRepeatError = true;
  }

  if (!(context.email && context.email.length > 0)) {
    valid = false;
    context.emailError = true;
  }
  return valid;
};

const isValidPassword = (context) => {
  const containsLetters = /[a-z]+/i.test(context.password);
  const containsNumbers = /\d/.test(context.password);
  const passwordLength = context.password.length >= 6;
  return (containsLetters === true && containsNumbers === true && passwordLength === true);
};

const createUser = (context, user) => {
  context.createUser({context: context, user}).then(() => {
    const msg = context.$t('user.successCreation', {name: user.name});
    context.$router.replace({name: 'UserList', params: {alert: msg}});
  }).catch((error) => {
    if (error.status === 409) {
      context.nameErrorUnique = true;
    } else if (error.status === 428) {
      context.passwordError = true;
    } else if (error.status === 400 && error.body.message.includes('not a nutritionist or prescriptor')) {
      context.prescriptorError = true;
    } else {
      showAlert(context, context.$t('user.errorCreation'), 'error');
    }
  });
};

const updateUser = (context, user) => {
  const msg = context.$t('user.successUpdate', {name: user.name});
  context.updateUser({context: context, user}).then(() => {
    context.$router.replace({name: 'UserList', params: {alert: msg}});
  }).catch((error) => {
    if (error.status === 409) {
      if (error.body.message.includes('Email')) {
        showAlert(context, context.$t('emailVerification.duplicatedEmail', {name: user.name}), 'error');
      }
    } else if (error.status === 400 && error.body.message.includes('not a nutritionist or prescriptor')) {
      context.prescriptorError = true;
    } else {
      showAlert(context, context.$t('user.errorUpdate', {name: user.name}), 'error');
    }
  });
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};

</script>

<style lang="scss" scoped>
@import "src/stylesheets/variables";

.variable-input-field {
  display: inline-block;
}

.variable-input-field-name {
  width: 100%;
  margin: 0 0;
}

.submit-buttons {
  float: right;
}

.explorer-controls {
  display: flex;
  align-items: center;
}

.filter {
  @media (max-width: $screen-l-max) {
    width: 100%;
    margin-bottom: $sp;
  }
}

</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/HealthUser.vue